import type { CatalogItem, ResourceRequirement } from "@vaulted/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { errorMessage, put } from "../api";
import { Alert, Button, Loading, Panel, PanelHeader } from "../components/ui";
import { useSession } from "../session";
import { RemoveButton } from "./settings/common";
import { useResources } from "./settings/ResourcesTab";

/** Equipment a service or add-on needs for a clinic visit. */
export function NeedsPanel({ item }: { item: CatalogItem }) {
  const qc = useQueryClient();
  const { can } = useSession();
  const manage = can("catalog.manage");
  const resources = useResources();
  const [rows, setRows] = useState<ResourceRequirement[]>(item.requirements);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => setRows(item.requirements), [item.requirements]);
  const save = useMutation({
    mutationFn: () => put(`/api/catalog/items/${item.id}/requirements`, { requirements: rows }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["catalog"] });
      setNotice("Saved.");
    },
  });
  const dirty = JSON.stringify(rows) !== JSON.stringify(item.requirements);
  if (resources.isLoading) return <Loading />;
  const types = resources.data?.types ?? [];
  const key = (r: ResourceRequirement) => (r.typeId ? `type:${r.typeId}` : `unit:${r.resourceId}`);
  const used = new Set(rows.map(key));
  const options = types.flatMap((t) => [
    { value: `type:${t.id}`, label: `Any ${t.name.toLowerCase()}` },
    ...t.units.map((u) => ({ value: `unit:${u.id}`, label: `${u.name} (specifically)` })),
  ]);
  const labelOf = (r: ResourceRequirement) => options.find((o) => o.value === key(r))?.label ?? "Unknown";
  const set = (next: ResourceRequirement[]) => {
    setNotice(null);
    setRows(next);
  };

  return (
    <Panel>
      <PanelHeader
        title="Needs"
        description={
          item.type === "service"
            ? "Equipment for a clinic visit, in use for the whole service. Mobile visits skip this."
            : "Extra equipment when this is added to a service, for the service's whole time."
        }
      />
      <form className="flex flex-col gap-3 px-5 py-5" onSubmit={(e) => (e.preventDefault(), save.mutate())}>
        {types.length === 0 && (
          <Alert tone="info">
            Add equipment under <Link to="/settings/resources" className="font-semibold underline">Settings → Resources</Link> first.
          </Alert>
        )}
        {rows.length === 0 && types.length > 0 && <p className="text-sm text-muted">No equipment needed.</p>}
        {rows.map((r, i) => (
          <div key={key(r)} className="flex flex-wrap items-center gap-3">
            <span className="min-w-48 font-medium">{labelOf(r)}</span>
            {r.typeId && (
              <label className="flex items-center gap-2 text-sm">
                Quantity
                <input
                  type="number"
                  min={1}
                  max={20}
                  disabled={!manage}
                  className="w-16 rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5 disabled:bg-mist"
                  value={r.quantity}
                  onChange={(e) => set(rows.map((x, j) => (j === i ? { ...x, quantity: Math.max(1, Number(e.target.value) || 1) } : x)))}
                />
              </label>
            )}
            {manage && <RemoveButton label={`Remove ${labelOf(r)}`} onClick={() => set(rows.filter((_, j) => j !== i))} />}
          </div>
        ))}
        {manage && options.some((o) => !used.has(o.value)) && (
          <select
            aria-label="Add equipment"
            className="self-start rounded-[var(--radius-control)] border border-line bg-white px-3 py-2 text-sm font-semibold text-brand"
            value=""
            onChange={(e) => {
              const [kind, id] = e.target.value.split(":");
              if (!id) return;
              set([...rows, { typeId: kind === "type" ? id : null, resourceId: kind === "unit" ? id : null, quantity: 1 }]);
            }}
          >
            <option value="">+ Add equipment…</option>
            {options
              .filter((o) => !used.has(o.value))
              .map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
          </select>
        )}
        {save.error ? <Alert>{errorMessage(save.error)}</Alert> : null}
        {notice && !dirty ? <Alert tone="ok">{notice}</Alert> : null}
        {manage && (
          <div className="flex justify-end">
            <Button variant="primary" type="submit" busy={save.isPending} disabled={!dirty}>
              Save needs
            </Button>
          </div>
        )}
      </form>
    </Panel>
  );
}
